// ─────────────────────────────────────────────────────────────────────────────
// KLOTHIQ — CompleteReturnModal
// ─────────────────────────────────────────────────────────────────────────────
// Final step of the return workflow (FEAT-SALE-STATUS-001), only reachable
// once a request has been accepted. Quantities were locked in at request
// time — this step only decides, per item, whether it came back in
// sellable condition (and should restock) or damaged, plus an optional
// refund. Wraps the previously-orphaned process_sale_return RPC.
// ─────────────────────────────────────────────────────────────────────────────

import UserGuide from '@/components/help/UserGuide';
import { Input, Select } from '@/components/ui/FormControls';
import { useEffect, useState } from 'react';
import { X, PackageCheck, Loader2, Check } from 'lucide-react';
import { useCompleteSaleReturn } from '@/hooks/useSales';
import { useCashAccounts } from '@/hooks/useCash';
import { toast } from 'sonner';

export interface AcceptedReturnItem {
  sale_item_id: string;
  product_name: string;
  sku?: string;
  variant_label?: string;
  quantity: number;
}

export interface CompleteReturnModalProps {
  isOpen: boolean;
  onClose: () => void;
  saleId: string;
  saleNumber: string;
  items: AcceptedReturnItem[];
  onSuccess?: () => void;
}

interface ItemDecision {
  condition: 'good' | 'damaged';
  restock: boolean;
}

export default function CompleteReturnModal({
  isOpen,
  onClose,
  saleId,
  saleNumber,
  items,
  onSuccess,
}: CompleteReturnModalProps) {
  const completeReturn = useCompleteSaleReturn();
  const { data: accounts } = useCashAccounts();
  const [decisions, setDecisions] = useState<Record<string, ItemDecision>>({});
  const [issueRefund, setIssueRefund] = useState(false);
  const [refundAccount, setRefundAccount] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setDecisions(Object.fromEntries(items.map((i) => [i.sale_item_id, { condition: 'good', restock: true }])));
      setIssueRefund(false);
      setRefundAccount('');
      setRefundAmount('');
      setError(null);
    }
  }, [isOpen, items]);

  if (!isOpen) return null;

  const setDecision = (id: string, patch: Partial<ItemDecision>) => {
    setDecisions((d) => ({ ...d, [id]: { ...d[id]!, ...patch } }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (issueRefund) {
      if (!refundAccount) { setError('Select a cash account for the refund'); return; }
      if (!parseFloat(refundAmount) || parseFloat(refundAmount) <= 0) { setError('Enter a valid refund amount'); return; }
    }

    try {
      await completeReturn.mutateAsync({
        sale_id: saleId,
        items: items.map((i) => ({
          sale_item_id: i.sale_item_id,
          condition: decisions[i.sale_item_id]?.condition ?? 'good',
          restock: decisions[i.sale_item_id]?.restock ?? true,
        })),
        refund_account_id: issueRefund ? refundAccount : undefined,
        refund_amount: issueRefund ? parseFloat(refundAmount) : undefined,
      });
      toast.success('Return completed');
      if (onSuccess) onSuccess();
      onClose();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to complete return');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm animate-in fade-in duration-150"
      role="dialog"
      aria-modal="true"
      aria-labelledby="complete-return-title" data-field-guide-scope
    >
      <div className="w-full max-w-lg rounded-xl border border-border bg-card p-5 sm:p-6 shadow-2xl space-y-4 text-foreground max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-border pb-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              <PackageCheck className="h-4 w-4" />
            </div>
            <div>
              <div className="flex items-start justify-between gap-3 pr-8">
                <h3 id="complete-return-title" className="text-base font-bold text-foreground">
                  Complete Return
                </h3>
                <UserGuide compact />
              </div>
              <p className="text-xs text-muted-foreground font-mono">Order {saleNumber}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={completeReturn.isPending}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors disabled:opacity-50" aria-label="Close modal">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3.5">
          <div className="space-y-2">
            <label className="text-xs font-semibold text-foreground">Condition of returned items</label>
            <div className="divide-y divide-border rounded-md border border-border">
              {items.map((item) => {
                const decision = decisions[item.sale_item_id] ?? { condition: 'good', restock: true };
                return (
                  <div key={item.sale_item_id} className="space-y-2 p-2.5">
                    <div>
                      <p className="truncate text-sm font-medium text-foreground">{item.product_name} × {item.quantity}</p>
                      <p className="text-[11px] text-muted-foreground">{[item.sku, item.variant_label].filter(Boolean).join(' · ')}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <Select
                        value={decision.condition}
                        disabled={completeReturn.isPending}
                        onChange={(e) => {
                          const condition = e.target.value as ItemDecision['condition'];
                          setDecision(item.sale_item_id, { condition, restock: condition === 'good' });
                        }}
                        className="h-8 rounded-md border border-input bg-background px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                        helpLabel="Condition of returned items"
                      >
                        <option value="good">Good condition</option>
                        <option value="damaged">Damaged</option>
                      </Select>
                      <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Input
                          type="checkbox"
                          checked={decision.restock}
                          disabled={completeReturn.isPending || decision.condition === 'damaged'}
                          onChange={(e) => setDecision(item.sale_item_id, { restock: e.target.checked })}
                          className="h-3.5 w-3.5 rounded border-input"
                          helpLabel="Add back to stock"
                        />
                        Add back to stock
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-2 rounded-md border border-border p-3">
            <label className="flex items-center gap-2 text-sm font-medium text-foreground">
              <Input
                type="checkbox"
                checked={issueRefund}
                disabled={completeReturn.isPending}
                onChange={(e) => setIssueRefund(e.target.checked)}
                className="h-4 w-4 rounded border-input"
                helpLabel="Issue a refund now"
              />
              Issue a refund now
            </label>
            {issueRefund && (
              <div className="grid grid-cols-2 gap-2 pt-1">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Account *</label>
                  <Select
                    value={refundAccount}
                    disabled={completeReturn.isPending}
                    onChange={(e) => setRefundAccount(e.target.value)}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    helpLabel="Account *"
                  >
                    <option value="">Select account</option>
                    {accounts?.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground">Amount (₹) *</label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    disabled={completeReturn.isPending}
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(e.target.value)}
                    placeholder="0.00"
                    className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    helpLabel="Amount (₹) *"
                  />
                </div>
              </div>
            )}
          </div>

          {error && <p className="text-xs text-destructive">{error}</p>}

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
            <button type="button" onClick={onClose} disabled={completeReturn.isPending}
              className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent text-foreground transition-colors disabled:opacity-50">
              Cancel
            </button>
            <button type="submit" disabled={completeReturn.isPending}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {completeReturn.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Complete Return
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
